import maxceiEmblem from "@assets/images/maxcei-emblem.png";
import { useUser } from "@dashboard/auth";
import { useLegacyThemeHandler } from "@dashboard/components/Sidebar/user/Controls";
import { operatorMessages } from "@dashboard/operator/messages";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { LogOut, Menu, Moon, Sun } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import { useLocation } from "react-router";
import { NavLink } from "react-router-dom";

import { portalMessages as m } from "./messages";
import { formatDateTime } from "./utils";

export interface PortalModule {
  id: string;
  path: string;
  title: MessageDescriptor;
  icon: ReactNode;
  section: "mine" | "manage";
}

export interface PortalLastLogin {
  at: string;
  platform: string;
  failedSince: number;
}

const PLACEHOLDER_EMAIL_SUFFIX = "@users.invalid";

const displayEmail = (email = "") =>
  email.endsWith(PLACEHOLDER_EMAIL_SUFFIX)
    ? email.slice(0, -PLACEHOLDER_EMAIL_SUFFIX.length)
    : email;

const LastLogin = ({ lastLogin }: { lastLogin: PortalLastLogin | null | undefined }) => {
  const intl = useIntl();

  if (!lastLogin) {
    return (
      <Text size={2} color="default2">
        {intl.formatMessage(m.firstLogin)}
      </Text>
    );
  }

  return (
    <Text size={2} color="default2">
      {intl.formatMessage(m.previousLogin, {
        when: formatDateTime(lastLogin.at),
        platform: lastLogin.platform || "—",
      })}
      {lastLogin.failedSince > 0 && (
        <Text as="span" size={2} color="critical1" fontWeight="bold">
          {" · "}
          {intl.formatMessage(m.failedSince, { count: lastLogin.failedSince })}
        </Text>
      )}
    </Text>
  );
};

const NavSection = ({
  title,
  modules,
  onNavigate,
}: {
  title: string;
  modules: PortalModule[];
  onNavigate: () => void;
}) => {
  const intl = useIntl();

  if (modules.length === 0) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Text size={1} color="default2" fontWeight="bold" paddingX={3} paddingY={2}>
        {title.toUpperCase()}
      </Text>
      {modules.map(module => (
        <NavLink
          key={module.id}
          to={module.path}
          exact={module.path === "/"}
          className="portal-nav-link"
          activeClassName="portal-nav-link--active"
          onClick={onNavigate}
        >
          {module.icon}
          <span>{intl.formatMessage(module.title)}</span>
        </NavLink>
      ))}
    </Box>
  );
};

/**
 * The portal's own chrome, like the dashboard: a sidebar grouped into the
 * colaborador's pages and management pages, and a slim top bar with the
 * previous sign-in (so a sign-in the person did not make stands out).
 */
export const PortalLayout = ({
  modules,
  children,
  actions,
  lastLogin,
}: {
  modules: PortalModule[];
  children: ReactNode;
  actions?: ReactNode;
  lastLogin?: PortalLastLogin | null;
}) => {
  const intl = useIntl();
  const location = useLocation();
  const { user, logout } = useUser();
  const { changeTheme, theme } = useLegacyThemeHandler();
  const [open, setOpen] = useState(false);
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || displayEmail(user?.email);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <Box className="portal-shell" backgroundColor="default1">
      <Box
        as="aside"
        className={`portal-sidebar${open ? " portal-sidebar--open" : ""}`}
        backgroundColor="default1"
        borderRightWidth={1}
        borderRightStyle="solid"
        borderColor="default1"
      >
        <Box display="flex" alignItems="center" gap={3} paddingX={3} paddingY={4}>
          <Box as="img" src={maxceiEmblem} alt="MAXCEI" width={8} height={8} />
          <Text size={4} fontWeight="bold">
            {intl.formatMessage(m.platformName)}
          </Text>
        </Box>
        <Box as="nav" display="flex" flexDirection="column" gap={4} paddingX={2}>
          <NavSection
            title={intl.formatMessage(m.sectionMine)}
            modules={modules.filter(module => module.section === "mine")}
            onNavigate={() => setOpen(false)}
          />
          <NavSection
            title={intl.formatMessage(m.sectionManage)}
            modules={modules.filter(module => module.section === "manage")}
            onNavigate={() => setOpen(false)}
          />
        </Box>
      </Box>
      {open && <div className="portal-backdrop" onClick={() => setOpen(false)} />}
      <Box className="portal-content">
        <Box
          as="header"
          className="portal-header"
          display="flex"
          alignItems="center"
          gap={3}
          paddingX={4}
          paddingY={3}
          borderBottomWidth={1}
          borderBottomStyle="solid"
          borderColor="default1"
        >
          <Box className="portal-menu-button">
            <Button
              variant="tertiary"
              size="small"
              icon={<Menu size={18} />}
              onClick={() => setOpen(true)}
              aria-label={intl.formatMessage(m.menu)}
            />
          </Box>
          <Box display="flex" flexDirection="column" __minWidth={0} __flex={1}>
            <Text size={3} fontWeight="medium" ellipsis>
              {displayName}
            </Text>
            <LastLogin lastLogin={lastLogin} />
          </Box>
          <Box className="portal-header-actions" display="flex" alignItems="center" gap={2}>
            {actions}
            <Button
              variant="tertiary"
              size="small"
              icon={theme === "defaultLight" ? <Moon size={16} /> : <Sun size={16} />}
              onClick={changeTheme}
              title={intl.formatMessage(operatorMessages.toggleTheme)}
              aria-label={intl.formatMessage(operatorMessages.toggleTheme)}
            />
            <Button
              variant="secondary"
              size="small"
              onClick={() => logout?.()}
              title={intl.formatMessage(operatorMessages.logout)}
            >
              <LogOut size={16} />
            </Button>
          </Box>
        </Box>
        <Box as="main" __flex={1}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};
